import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { users, initialTimesheetEntries } from './dummyData';
import Timesheet from './components/Timesheet';
import Dashboard from './components/Dashboard';
import Approvals from './components/Approvals';
import ProjectActivity from './components/ProjectActivity';
import TeamStatus from './components/TeamStatus';
import { Building2, LayoutDashboard, Clock, CheckSquare, Activity, Users, X, Info, CheckCircle2, AlertCircle, Settings2, BarChart3 } from 'lucide-react';
import PersonnelAdmin from './components/PersonnelAdmin';
import ConsolidatedReport from './components/ConsolidatedReport';
import ManagerAllocation from './components/ManagerAllocation';

function Navigation({ userRole, onMobileClick }) {
  const location = useLocation();
  const navItems = [
    { path: '/', label: 'Overview', icon: LayoutDashboard, roles: ['Admin', 'Management', 'Normal'] },
    { path: '/reports', label: 'Financial Reports', icon: BarChart3, roles: ['Admin'] },
    { path: '/timesheet', label: 'Timesheet', icon: Clock, roles: ['Admin', 'Management', 'Normal'] },
    { path: '/approvals', label: 'Approvals', icon: CheckSquare, roles: ['Admin', 'Management'] },
    { path: '/activity', label: 'Project Activity', icon: Activity, roles: ['Admin', 'Management', 'Normal'] },
    { path: '/personnel', label: 'Personnel', icon: Users, roles: ['Admin'] },
    { path: '/allocation', label: 'Manager Allocation', icon: Settings2, roles: ['Admin'] },
    { path: '/team', label: 'Team Status', icon: Users, roles: ['Admin', 'Management'] },
  ];

  const visibleItems = navItems.filter(item => item.roles.includes(userRole));

  return (
    <nav className="flex flex-col gap-2 mt-8 px-4">
      {visibleItems.map((item) => {
        const Icon = item.icon;
        const isActive = location.pathname === item.path;
        return (
          <Link
            key={item.path}
            to={item.path}
            onClick={() => onMobileClick && onMobileClick()}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
              isActive 
                ? 'bg-indigo-50 text-indigo-700 font-bold' 
                : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800 font-medium'
            }`}
          >
            <Icon className="w-5 h-5" />
            {item.label}
          </Link>
        );
      })}
    </nav>
  );
}

export default function AppWrapper() {
  return (
    <BrowserRouter>
      <App />
    </BrowserRouter>
  );
}

function App() {
  // Global state simulating backend persistence
  const [entries, setEntries] = useState(initialTimesheetEntries);
  // Simulating reactive user database
  const [projectUsers, setProjectUsers] = useState(users);
  // Simulating a logged-in user
  const [activeUserId, setActiveUserId] = useState(users[0].id);
  // Notification state
  const [toasts, setToasts] = useState([]);

  const activeUser = projectUsers.find((u) => u.id === activeUserId);

  const showToast = (message, type = 'success') => {
    const id = Date.now();
    const newToast = { id, message, type };
    setToasts((prev) => [...prev, newToast]);
    
    // Auto-remove after 3 seconds
    setTimeout(() => {
      setToasts((prev) => prev.filter(t => t.id !== id));
    }, 3000);
  };

  const removeToast = (id) => {
    setToasts((prev) => prev.filter(t => t.id !== id));
  };

  const handleSaveEntries = (newEntries) => {
    setEntries([...entries, ...newEntries]);
    showToast(`Successfully submitted ${newEntries.length} timesheet entries.`, 'success');
  };

  const handleApprovalAction = (id, action) => {
    setEntries(entries.map(e => 
      e.id === id ? { ...e, status: action === 'approve' ? 'Approved' : 'Rejected' } : e
    ));
    showToast(`Timesheet ${action === 'approve' ? 'Approved' : 'Rejected'} successfully.`, action === 'approve' ? 'success' : 'error');
  };

  const handleUpdateUsers = (updatedUsers) => {
    setProjectUsers(updatedUsers);
    showToast('Personnel settings updated successfully.', 'success');
  };

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-50 flex font-sans">
      {/* Sidebar Navigation (Desktop) */}
      <aside className="w-64 bg-white border-r border-slate-200 hidden md:flex flex-col">
        <div className="h-16 flex items-center px-6 border-b border-slate-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="bg-indigo-600 p-2 rounded-lg text-white shadow-md">
              <Building2 className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-800 tracking-tight leading-tight">JV Track</h1>
              <p className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Project Controls</p>
            </div>
          </div>
        </div>
        <Navigation userRole={activeUser?.userRole} />
      </aside>

      {/* Sidebar Drawer (Mobile) */}
      {isSidebarOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div 
            className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm" 
            onClick={() => setIsSidebarOpen(false)}
          ></div>
          <aside className="fixed inset-y-0 left-0 w-72 bg-white shadow-2xl animate-in slide-in-from-left duration-300 flex flex-col">
            <div className="h-16 flex items-center justify-between px-6 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <div className="bg-indigo-600 p-2 rounded-lg text-white shadow-md">
                  <Building2 className="w-5 h-5" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-slate-800 tracking-tight leading-tight">JV Track</h1>
                </div>
              </div>
              <button 
                onClick={() => setIsSidebarOpen(false)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto py-4">
              <Navigation userRole={activeUser?.userRole} onMobileClick={() => setIsSidebarOpen(false)} />
            </div>
          </aside>
        </div>
      )}

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* Top Header */}
        <header className="bg-white border-b border-slate-200 h-16 flex items-center justify-between px-4 sm:px-6 sticky top-0 z-10 flex-shrink-0">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setIsSidebarOpen(true)}
              className="p-2 md:hidden text-slate-500 hover:bg-slate-50 rounded-lg"
            >
              <Activity className="w-6 h-6 rotate-90" />
            </button>
            <div className="flex items-center gap-2">
              <div className="bg-indigo-600 p-1.5 rounded-md text-white md:hidden">
                <Building2 className="w-4 h-4" />
              </div>
              <h1 className="text-base font-bold text-slate-800 md:hidden">JV Track</h1>
            </div>
          </div>
          
          {/* Login Simulator */}
          <div className="flex items-center justify-end gap-2 sm:gap-3">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider hidden lg:block">Simulate Login:</label>
            <select
              value={activeUserId}
              onChange={(e) => setActiveUserId(e.target.value)}
              className="bg-slate-50 border border-slate-200 text-slate-800 rounded-lg py-1.5 px-2 sm:px-3 text-xs sm:text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none cursor-pointer hover:bg-slate-100 transition-colors shadow-sm font-medium max-w-[150px] sm:max-w-none"
              title="Switch User to see different Scopes"
            >
              {projectUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-6 lg:p-8 max-w-7xl mx-auto w-full overflow-y-auto">
          <Routes>
            <Route path="/" element={
                <section>
                  <Dashboard entries={entries} activeUser={activeUser} users={projectUsers} />
                </section>
            } />
            <Route path="/timesheet" element={
              <section>
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-slate-800 tracking-tight">Time Entry</h2>
                  <p className="text-slate-500 text-sm font-medium">Submit your hours to your designated WBS scope.</p>
                </div>
                {activeUser && (
                  <Timesheet 
                    activeUser={activeUser} 
                    entries={entries}
                    onSave={handleSaveEntries} 
                    showToast={showToast}
                  />
                )}
              </section>
            } />
            <Route path="/approvals" element={
              ['Admin', 'Management'].includes(activeUser?.userRole) ? (
                <Approvals 
                  timesheets={entries} 
                  users={projectUsers}
                  onAction={handleApprovalAction} 
                  showToast={showToast}
                />
              ) : <Navigate to="/timesheet" replace />
            } />
            <Route path="/activity" element={
               <ProjectActivity entries={entries} users={projectUsers} />
            } />
            <Route path="/personnel" element={
               activeUser?.userRole === 'Admin' ? (
                 <PersonnelAdmin users={projectUsers} onUpdateUsers={handleUpdateUsers} />
               ) : <Navigate to="/timesheet" replace />
            } />
            <Route path="/allocation" element={
               activeUser?.userRole === 'Admin' ? (
                 <ManagerAllocation users={projectUsers} onUpdateUsers={handleUpdateUsers} />
               ) : <Navigate to="/timesheet" replace />
            } />
            <Route path="/dashboard" element={<Navigate to="/" replace />} />
            <Route path="/team-status" element={<Navigate to="/team" replace />} />
            <Route path="/team" element={
               ['Admin', 'Management'].includes(activeUser?.userRole) ? (
                 <TeamStatus users={projectUsers} />
               ) : <Navigate to="/timesheet" replace />
            } />
            <Route path="/reports" element={
              activeUser?.userRole === 'Admin' ? (
                <ConsolidatedReport />
              ) : <Navigate to="/" replace />
            } />
            <Route path="*" element={<Navigate to={activeUser?.userRole === 'Normal' ? "/timesheet" : "/"} replace />} />
          </Routes>
        </div>
      </main>

      {/* Toast Notification Container */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-3 pointer-events-none">
        {toasts.map((toast) => (
          <div 
            key={toast.id}
            className={`pointer-events-auto flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border animate-in slide-in-from-right-full duration-300 min-w-[300px] ${
              toast.type === 'success' 
                ? 'bg-emerald-50 border-emerald-100 text-emerald-800' 
                : toast.type === 'error'
                  ? 'bg-rose-50 border-rose-100 text-rose-800'
                  : 'bg-white border-slate-200 text-slate-800'
            }`}
          >
            {toast.type === 'success' && <CheckCircle2 className="w-5 h-5 text-emerald-500" />}
            {toast.type === 'error' && <AlertCircle className="w-5 h-5 text-rose-500" />}
            {toast.type === 'info' && <Info className="w-5 h-5 text-indigo-500" />}
            
            <p className="text-sm font-bold flex-1">{toast.message}</p>
            
            <button 
              onClick={() => removeToast(toast.id)}
              className="p-1 hover:bg-black/5 rounded-lg transition-colors"
            >
              <X className="w-4 h-4 opacity-50" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
